/**
 * Toaster — mount once per app. Sets that app's toast defaults in one place;
 * per-call `behavior` overrides win for individual toasts.
 *
 * Curated prop surface over sonner's <Toaster/> with Butter defaults pre-filled.
 */
import { Toaster as SonnerToaster } from 'sonner'

import type { ToastPosition } from './types'

export type ToastOffset =
  | string
  | number
  | { top?: string | number; right?: string | number; bottom?: string | number; left?: string | number }

/** Butter's edge offset, in px. Applies to every side unless a prop overrides it. */
const DEFAULT_OFFSET = 16

/**
 * Fill a partial offset object out to all four sides.
 *
 * sonner only applies its own default when the whole prop is absent: hand it
 * `{ top }` and the other three sides fall through to sonner's internals (32px
 * desktop, 16px mobile), not ours. Callers overriding one side almost always
 * mean "…and leave the rest alone", so pin the remaining sides to our default
 * and keep `@default 16` true whatever shape the prop arrives in.
 */
function withDefaultSides(offset: ToastOffset): ToastOffset {
  if (typeof offset !== 'object') return offset
  return {
    top: DEFAULT_OFFSET,
    right: DEFAULT_OFFSET,
    bottom: DEFAULT_OFFSET,
    left: DEFAULT_OFFSET,
    ...offset,
  }
}

export interface ToasterProps {
  /** @default 'top-right' */
  position?: ToastPosition
  /** Default auto-dismiss in ms. @default 5000 */
  duration?: number
  /** Gap between stacked toasts, px. @default 12 */
  gap?: number
  /**
   * Offset from the viewport edge. Bare numbers are px; an object sets
   * individual sides and leaves the rest at the default. @default 16
   */
  offset?: ToastOffset
  /**
   * Offset from the viewport edge below 600px width. Load-bearing on phones:
   * sonner's mobile media query reads `--mobile-offset-*`, which only this prop
   * feeds, so `offset` alone has no effect at that breakpoint. @default 16
   */
  mobileOffset?: ToastOffset
  /** Max toasts shown at once. @default 4 */
  visibleToasts?: number
}

export function Toaster({
  position = 'top-right',
  duration = 5000,
  gap = 12,
  offset = DEFAULT_OFFSET,
  mobileOffset = DEFAULT_OFFSET,
  visibleToasts = 4,
}: ToasterProps) {
  // Stop toast interactions from bubbling past the toast layer. Toasts are a
  // system overlay: dismissing one must mean nothing to the page beneath it.
  // Without this, every Radix dismissable layer (drawers, dialogs, sheets —
  // which listen for pointerdown/focusin at `document`) counts a toast click
  // as an "outside interaction" and closes itself, discarding form state.
  // Stopping here works because React delegates events at the app root, which
  // sits below `document` — so one firewall covers every modal in every app,
  // with no per-layer onInteractOutside guards.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation()

  return (
    // display: contents — the firewall must not generate a box: a box inside a
    // transformed ancestor would become the containing block for sonner's
    // position: fixed viewport and drag the toasts out of place. Events still
    // propagate through a boxless element, so the handlers keep working.
    <div style={{ display: 'contents' }} onPointerDown={stop} onMouseDown={stop} onClick={stop} onFocus={stop}>
      <SonnerToaster
        position={position}
        duration={duration}
        gap={gap}
        offset={withDefaultSides(offset)}
        mobileOffset={withDefaultSides(mobileOffset)}
        visibleToasts={visibleToasts}
        // butterToast renders via toast.custom, which sonner already marks
        // data-styled="false" — so ToastView owns the box with no built-in chrome.
        // We intentionally leave `unstyled` off: it's a no-op for our custom toasts
        // but keeps a safety net so a stray non-custom sonner.toast() still renders
        // with sonner's defaults instead of bare.
        //
        // pointer-events-auto: a Radix MODAL layer puts `pointer-events: none` on
        // <body> while open; toasts inherit it and every click falls straight
        // through to the page. The element-level utility opts each toast back in
        // regardless of body state (the firewall above keeps those clicks from
        // meaning anything to the modal).
        toastOptions={{ classNames: { toast: 'w-full pointer-events-auto' } }}
      />
    </div>
  )
}
